import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { handleAxiosError } from '../../utils/helpers';
import { getAreaSignalStatistics, getColumnMappings, getGeneralSeaAreas, requestActivityReport } from './downloadApi';
import { TGeneralSeaAreas, TReportRequestParams, actions, TColumnMappings } from './types/downloadTypes';
import { PayloadAction } from '@reduxjs/toolkit';
import { columnMappingsActions } from './slices/columnMappingsSlice';
import { generalSeaAreasActions } from './slices/generalSeaAreasSlice';
import { activityReportActions } from './slices/activityReportSlice';
import { areaSignalStatisticsActions } from './slices/areaSignalStatisticsSlice';
import { 
    trackEvent,
    trackExceptionWithContext
} from '../../services/application-insights/appInsights';
import { 
    AppInsightsActions, 
    AppInsightsComponents, 
    AppInsightsErrorTypes 
} from '../../services/application-insights/appInsightsConstants';

function* getGeneralSeaAreasSaga(): Generator {
    const requestId = `sea-areas-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
        trackEvent(
            AppInsightsActions.DOWNLOAD_INITIATE,
            {
                requestId,
                component: AppInsightsComponents.DOWNLOAD_COMPONENT,
                downloadType: "general-sea-areas"
            }
        );

        const response: TGeneralSeaAreas[] = yield call(getGeneralSeaAreas);
        yield put(generalSeaAreasActions.getGeneralSeaAreasSuccessAction(response));

        trackEvent(
            AppInsightsActions.DOWNLOAD_SUCCESS,
            {
                requestId,
                component: AppInsightsComponents.DOWNLOAD_COMPONENT,
                downloadType: "general-sea-areas",
                areasCount: response.length,
                responseSize: JSON.stringify(response).length
            }
        );
    } catch (error: any) {
        trackExceptionWithContext(
            error instanceof Error ? error : new Error(String(error)),
            {
                component: AppInsightsComponents.DOWNLOAD_COMPONENT,
                action: AppInsightsActions.DOWNLOAD_INITIATE,
                additionalData: {
                    requestId,
                    errorType: AppInsightsErrorTypes.API_ERROR,
                    downloadType: "general-sea-areas"
                }
            }
        );
        yield put(generalSeaAreasActions.getGeneralSeaAreasErrorAction(handleAxiosError(error)));
    }
}

function* submitActivityReportSaga(action: PayloadAction<TReportRequestParams>): Generator {
    const requestId = `activity-report-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
        trackEvent(
            AppInsightsActions.DOWNLOAD_ACTIVITY_REPORT,
            {
                requestId,
                component: AppInsightsComponents.DOWNLOAD_COMPONENT,
                reportType: action.payload.reportType,
                imoListLength: action.payload.imoList?.length || 0,
                hasDateRange: !!(action.payload.startDate && action.payload.endDate)
            }
        );

        const response: any = yield call(requestActivityReport, action.payload);
        yield put(activityReportActions.submitActivityReportSuccessAction(response));

        trackEvent(
            AppInsightsActions.DOWNLOAD_SUCCESS,
            {
                requestId,
                component: AppInsightsComponents.DOWNLOAD_COMPONENT,
                reportType: action.payload.reportType,
                dataReceived: !!response,
                responseSize: JSON.stringify(response).length
            }
        );
    } catch (error: any) {
        trackExceptionWithContext(
            error instanceof Error ? error : new Error(String(error)),
            {
                component: AppInsightsComponents.DOWNLOAD_COMPONENT,
                action: AppInsightsActions.DOWNLOAD_ACTIVITY_REPORT,
                additionalData: {
                    requestId,
                    errorType: AppInsightsErrorTypes.API_ERROR,
                    reportType: action.payload.reportType,
                    imoListLength: action.payload.imoList?.length || 0
                }
            }
        );
        yield put(activityReportActions.submitActivityReportErrorAction(handleAxiosError(error)));
    }
}

function* getColumnMappingsSaga(): Generator {
    const requestId = `column-mappings-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
        trackEvent(
            AppInsightsActions.DOWNLOAD_INITIATE,
            {
                requestId,
                component: AppInsightsComponents.DOWNLOAD_COMPONENT,
                downloadType: "column-mappings"
            }
        );

        const response: TColumnMappings[] = yield call(getColumnMappings);
        yield put(columnMappingsActions.getColumnMappingsSuccessAction(response));

        trackEvent(
            AppInsightsActions.DOWNLOAD_SUCCESS,
            {
                requestId,
                component: AppInsightsComponents.DOWNLOAD_COMPONENT,
                downloadType: "column-mappings",
                mappingsCount: response.length,
                responseSize: JSON.stringify(response).length
            }
        );
    } catch (error: any) {
        trackExceptionWithContext(
            error instanceof Error ? error : new Error(String(error)),
            {
                component: AppInsightsComponents.DOWNLOAD_COMPONENT,
                action: AppInsightsActions.DOWNLOAD_INITIATE,
                additionalData: {
                    requestId,
                    errorType: AppInsightsErrorTypes.API_ERROR,
                    downloadType: "column-mappings"
                }
            }
        );
        yield put(columnMappingsActions.getColumnMappingsErrorAction(handleAxiosError(error)));
    }
}

function* getAreaSignalStatisticsSaga(action: PayloadAction<{ lat: number; latTo: number; lon: number; lonTo: number }>): Generator {
    const requestId = `area-signal-stats-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    try {
        const { lat, latTo, lon, lonTo } = action.payload;
        
        trackEvent(
            AppInsightsActions.DOWNLOAD_AREA_STATISTICS,
            {
                requestId,
                component: AppInsightsComponents.DOWNLOAD_COMPONENT,
                lat: lat,
                latTo: latTo,
                lon: lon,
                lonTo: lonTo,
                areaSize: Math.abs(latTo - lat) * Math.abs(lonTo - lon)
            }
        );

        const response = yield call(getAreaSignalStatistics, lat, latTo, lon, lonTo);
        yield put(areaSignalStatisticsActions.getAreaSignalStatisticsSuccessAction(response));

        trackEvent(
            AppInsightsActions.DOWNLOAD_SUCCESS,
            {
                requestId,
                component: AppInsightsComponents.DOWNLOAD_COMPONENT,
                downloadType: "area-signal-statistics",
                dataReceived: !!response,
                responseSize: JSON.stringify(response).length
            }
        );
    } catch (error: any) {
        trackExceptionWithContext(
            error instanceof Error ? error : new Error(String(error)),
            {
                component: AppInsightsComponents.DOWNLOAD_COMPONENT,
                action: AppInsightsActions.DOWNLOAD_AREA_STATISTICS,
                additionalData: {
                    requestId,
                    errorType: AppInsightsErrorTypes.API_ERROR,
                    coordinates: `${action.payload.lat},${action.payload.latTo},${action.payload.lon},${action.payload.lonTo}`
                }
            }
        );
        yield put(areaSignalStatisticsActions.getAreaSignalStatisticsErrorAction(handleAxiosError(error)));
    }
}

export function* watchDownloadActions(): Generator {
    yield takeLatest(actions.GET_GENERAL_SEA_AREAS_REQUEST, getGeneralSeaAreasSaga);
    yield takeEvery(actions.SUBMIT_ACTIVITY_REPORT_REQUEST, submitActivityReportSaga); // takeEvery: each report submission is independent
    yield takeLatest(actions.GET_COLUMN_MAPPINGS_REQUEST, getColumnMappingsSaga);
    yield takeLatest(actions.GET_AREA_SIGNAL_STATISTICS_REQUEST, getAreaSignalStatisticsSaga);
}